import { ApiError } from '@/api/client'
import { useBootstrap, useBootstrapStatus, useLogin } from '@/api/hooks'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { FormError, PageLoader } from '@/components/ui/Feedback'
import { Input } from '@/components/ui/Field'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { ChefHat } from 'lucide-react'
import { useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  useDocumentTitle(title)
  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2.5">
          <span
            aria-hidden
            className="grid size-9 place-items-center rounded-[var(--radius-field)] bg-accent text-on-accent"
          >
            <ChefHat size={19} />
          </span>
          <span className="text-base font-semibold tracking-tight text-ink">Meal Planner</span>
        </div>
        <Card className="p-6">
          <h1 className="text-lg font-semibold tracking-tight text-ink">{title}</h1>
          <p className="mt-1 text-sm text-pretty text-ink-faint">{subtitle}</p>
          <div className="mt-5">{children}</div>
        </Card>
      </div>
    </div>
  )
}

/** Sign in. Sends first-time visitors to setup instead. */
export function LoginPage() {
  const status = useBootstrapStatus()
  const login = useLogin()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  if (status.isPending) {
    return <PageLoader label="Checking…" />
  }
  if (status.data?.needs_bootstrap) {
    return <Navigate to="/setup" replace />
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    try {
      await login.mutateAsync({ username, password })
      navigate('/recipes', { replace: true })
    } catch {
      // Rendered from the mutation's error below.
    }
  }

  const error = login.error
  const message =
    error instanceof ApiError ? error.message : error ? 'Could not sign in.' : null

  return (
    <AuthLayout title="Sign in" subtitle="Use your Meal Planner account.">
      <form onSubmit={submit} className="space-y-3">
        <FormError>{message}</FormError>
        <Input
          label="Username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          autoFocus
          required
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          loading={login.isPending}
        >
          Sign in
        </Button>
      </form>
      <p className="mt-4 text-center text-xs text-ink-faint">
        <Link to="/setup" className="hover:text-accent">
          First time here?
        </Link>
      </p>
    </AuthLayout>
  )
}

/** First-run setup: creates the admin account. */
export function SetupPage() {
  const status = useBootstrapStatus()
  const bootstrap = useBootstrap()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  if (status.isPending) {
    return <PageLoader label="Checking…" />
  }
  if (status.data && !status.data.needs_bootstrap) {
    return <Navigate to="/login" replace />
  }

  const mismatch = confirm.length > 0 && confirm !== password
  const tooShort = password.length > 0 && password.length < 10

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (mismatch || tooShort) return
    try {
      await bootstrap.mutateAsync({
        username,
        display_name: displayName || username,
        password,
      })
      navigate('/recipes', { replace: true })
    } catch {
      // Shown below.
    }
  }

  const error = bootstrap.error
  const message =
    error instanceof ApiError ? error.message : error ? 'Could not create the account.' : null

  return (
    <AuthLayout
      title="Create the first account"
      subtitle="This becomes the administrator. Further accounts are created from the Users page."
    >
      <form onSubmit={submit} className="space-y-3">
        <FormError>{message}</FormError>
        <Input
          label="Username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          autoFocus
          required
        />
        <Input
          label="Display name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="Optional"
          autoComplete="name"
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          error={tooShort ? 'Use at least 10 characters.' : undefined}
          required
        />
        <Input
          label="Repeat password"
          type="password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="new-password"
          error={mismatch ? 'The two passwords do not match.' : undefined}
          required
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          loading={bootstrap.isPending}
          disabled={mismatch || tooShort || !username || !password}
        >
          Create account
        </Button>
      </form>
      <p className="mt-4 text-center text-xs text-ink-faint">
        <Link to="/login" className="hover:text-accent">
          Already have an account?
        </Link>
      </p>
    </AuthLayout>
  )
}